import React, { useRef, useEffect, useState } from 'react';
import * as d3 from 'd3';

const CELL_SIZE = 120;
const CELL_PAD = 24;
const LABEL_SIZE = 40;

const styles = {
  card: {
    position: 'relative',
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '24px',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  title: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#1f2937',
    marginBottom: '2px',
  },
  subtitle: {
    fontSize: '12px',
    color: '#9ca3af',
    marginBottom: '16px',
  },
  legend: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '12px',
    marginBottom: '12px',
    alignItems: 'center',
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '12px',
    fontWeight: 500,
    color: '#374151',
  },
  legendDot: {
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    flexShrink: 0,
  },
  container: {
    overflowX: 'auto',
  },
  tooltip: {
    position: 'fixed',
    pointerEvents: 'none',
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    padding: '8px 12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
    fontSize: '12px',
    zIndex: 1000,
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    maxWidth: '260px',
  },
  emptyState: {
    color: '#6b7280',
    textAlign: 'center',
    padding: '40px 0',
  },
};

function getCorrelationColor(r) {
  if (r == null) return '#9ca3af';
  const absR = Math.abs(r);
  if (absR > 0.8) return '#1e40af';
  if (absR > 0.6) return '#3b82f6';
  if (absR > 0.4) return '#f59e0b';
  return '#9ca3af';
}

function getCorrelationKey(f1, f2, correlations) {
  const key1 = `${f1}_${f2}`;
  const key2 = `${f2}_${f1}`;
  if (correlations[key1] != null) return correlations[key1];
  if (correlations[key2] != null) return correlations[key2];
  return null;
}

export default function ScatterMatrix({ data }) {
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const [tooltip, setTooltip] = useState(null);

  useEffect(() => {
    if (!data || !data.points || data.points.length === 0 || !data.fields || data.fields.length === 0) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const { fields, labels, points, correlations } = data;
    const n = fields.length;
    const totalSize = LABEL_SIZE + n * CELL_SIZE + (n - 1) * 2;

    svg
      .attr('viewBox', `0 0 ${totalSize} ${totalSize}`)
      .attr('width', '100%')
      .style('max-width', `${totalSize}px`);

    // State color scale
    const states = [...new Set(points.map((d) => d.state))].sort();
    const stateColor = d3.scaleOrdinal(d3.schemeTableau10).domain(states);

    // Build per-field scales (x goes left-to-right, y goes bottom-to-top)
    const xScales = {};
    const yScales = {};
    fields.forEach((field) => {
      const vals = points.map((d) => d[field]).filter((v) => v != null && typeof v === 'number');
      const ext = d3.extent(vals);
      const pad = (ext[1] - ext[0]) * 0.1 || 1;
      xScales[field] = d3.scaleLinear()
        .domain([ext[0] - pad, ext[1] + pad])
        .range([CELL_PAD / 2, CELL_SIZE - CELL_PAD / 2]);
      yScales[field] = d3.scaleLinear()
        .domain([ext[0] - pad, ext[1] + pad])
        .range([CELL_SIZE - CELL_PAD / 2, CELL_PAD / 2]);
    });

    // Helper to get cell origin
    function cellX(col) {
      return LABEL_SIZE + col * (CELL_SIZE + 2);
    }
    function cellY(row) {
      return LABEL_SIZE + row * (CELL_SIZE + 2);
    }

    // Draw each cell
    for (let row = 0; row < n; row++) {
      for (let col = 0; col < n; col++) {
        const ox = cellX(col);
        const oy = cellY(row);
        const fieldX = fields[col];
        const fieldY = fields[row];

        // Cell background
        svg.append('rect')
          .attr('x', ox)
          .attr('y', oy)
          .attr('width', CELL_SIZE)
          .attr('height', CELL_SIZE)
          .attr('fill', row === col ? '#f9fafb' : '#fff')
          .attr('stroke', '#e5e7eb')
          .attr('stroke-width', 0.5)
          .attr('rx', 2);

        if (row === col) {
          // Diagonal: field label
          const label = (labels && labels[fieldX]) || fieldX;
          svg.append('text')
            .attr('x', ox + CELL_SIZE / 2)
            .attr('y', oy + CELL_SIZE / 2)
            .attr('text-anchor', 'middle')
            .attr('dominant-baseline', 'central')
            .attr('font-size', '13px')
            .attr('font-weight', 600)
            .attr('fill', '#374151')
            .attr('font-family', "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif")
            .text(label);
        } else if (row > col) {
          // Lower triangle: scatter plot

          // Clip path for this cell
          const clipId = `clip-${row}-${col}`;
          svg.append('defs')
            .append('clipPath')
            .attr('id', clipId)
            .append('rect')
            .attr('x', ox)
            .attr('y', oy)
            .attr('width', CELL_SIZE)
            .attr('height', CELL_SIZE);

          const cellGroup = svg.append('g').attr('clip-path', `url(#${clipId})`);

          const xSc = xScales[fieldX];
          const ySc = yScales[fieldY];

          points.forEach((pt) => {
            const vx = pt[fieldX];
            const vy = pt[fieldY];
            if (vx == null || vy == null || typeof vx !== 'number' || typeof vy !== 'number') return;

            cellGroup.append('circle')
              .attr('cx', ox + xSc(vx))
              .attr('cy', oy + ySc(vy))
              .attr('r', 3)
              .attr('fill', stateColor(pt.state))
              .attr('opacity', 0.7)
              .style('cursor', 'pointer')
              .on('mousemove', (event) => {
                d3.select(event.currentTarget).attr('r', 5).attr('opacity', 1);
                setTooltip({
                  x: event.clientX + 12,
                  y: event.clientY - 10,
                  item: pt,
                });
              })
              .on('mouseleave', (event) => {
                d3.select(event.currentTarget).attr('r', 3).attr('opacity', 0.7);
                setTooltip(null);
              });
          });
        } else {
          // Upper triangle: correlation value
          const r = getCorrelationKey(fieldX, fieldY, correlations || {});
          const color = getCorrelationColor(r);

          svg.append('text')
            .attr('x', ox + CELL_SIZE / 2)
            .attr('y', oy + CELL_SIZE / 2 - 6)
            .attr('text-anchor', 'middle')
            .attr('dominant-baseline', 'central')
            .attr('font-size', '18px')
            .attr('font-weight', 700)
            .attr('fill', color)
            .attr('font-family', "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif")
            .text(r != null ? r.toFixed(2) : '--');

          svg.append('text')
            .attr('x', ox + CELL_SIZE / 2)
            .attr('y', oy + CELL_SIZE / 2 + 12)
            .attr('text-anchor', 'middle')
            .attr('dominant-baseline', 'central')
            .attr('font-size', '9px')
            .attr('fill', '#9ca3af')
            .attr('font-family', "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif")
            .text('r');
        }
      }
    }

    // Row labels (left side)
    fields.forEach((field, i) => {
      const label = (labels && labels[field]) || field;
      svg.append('text')
        .attr('x', LABEL_SIZE - 6)
        .attr('y', cellY(i) + CELL_SIZE / 2)
        .attr('text-anchor', 'end')
        .attr('dominant-baseline', 'central')
        .attr('font-size', '11px')
        .attr('font-weight', 600)
        .attr('fill', '#6b7280')
        .attr('font-family', "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif")
        .text(label);
    });

    // Column labels (top)
    fields.forEach((field, i) => {
      const label = (labels && labels[field]) || field;
      svg.append('text')
        .attr('x', cellX(i) + CELL_SIZE / 2)
        .attr('y', LABEL_SIZE - 8)
        .attr('text-anchor', 'middle')
        .attr('dominant-baseline', 'auto')
        .attr('font-size', '11px')
        .attr('font-weight', 600)
        .attr('fill', '#6b7280')
        .attr('font-family', "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif")
        .text(label);
    });

    return () => {
      svg.selectAll('*').remove();
    };
  }, [data]);

  if (!data || !data.points || data.points.length === 0) {
    return (
      <div style={styles.card}>
        <div style={styles.title}>Price Field Relationships</div>
        <div style={styles.subtitle}>Pairwise scatter plots between service pricing fields</div>
        <div style={styles.emptyState}>No scatter matrix data available</div>
      </div>
    );
  }

  // Build legend from top states
  const stateCounts = {};
  data.points.forEach((d) => {
    stateCounts[d.state] = (stateCounts[d.state] || 0) + 1;
  });
  const topStates = Object.entries(stateCounts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5)
    .map(([state]) => state);

  const allStates = [...new Set(data.points.map((d) => d.state))].sort();
  const stateColor = d3.scaleOrdinal(d3.schemeTableau10).domain(allStates);

  const fieldLabels = data.labels || {};

  return (
    <div style={styles.card}>
      <div style={styles.title}>Price Field Relationships</div>
      <div style={styles.subtitle}>Pairwise scatter plots between service pricing fields</div>
      <div style={styles.legend}>
        {topStates.map((state) => (
          <div key={state} style={styles.legendItem}>
            <span
              style={{
                ...styles.legendDot,
                background: stateColor(state),
              }}
            />
            <span>{state}</span>
          </div>
        ))}
        {allStates.length > topStates.length && (
          <div style={{ ...styles.legendItem, color: '#9ca3af' }}>
            +{allStates.length - topStates.length} more
          </div>
        )}
      </div>
      <div style={styles.container} ref={containerRef}>
        <svg ref={svgRef} />
      </div>
      {tooltip && (
        <div
          style={{
            ...styles.tooltip,
            left: tooltip.x,
            top: tooltip.y,
          }}
        >
          <div style={{ fontWeight: 600, color: '#1f2937', marginBottom: '4px' }}>
            {tooltip.item.label}
          </div>
          <div style={{ fontSize: '11px', color: '#9ca3af', marginBottom: '6px' }}>
            {tooltip.item.state}
          </div>
          {data.fields.map((field) => {
            const val = tooltip.item[field];
            const label = fieldLabels[field] || field;
            return (
              <div
                key={field}
                style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}
              >
                <span style={{ color: '#6b7280' }}>{label}</span>
                <span style={{ fontWeight: 600 }}>
                  {val != null && typeof val === 'number' ? `$${val.toFixed(2)}` : '--'}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
